import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronDown, ClipboardList, HelpCircle, FileUp, GraduationCap, LayoutTemplate } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import * as AuthModule from 'modules/auth';
import { useFeature, FEATURES } from 'modules/entitlements';
import { PERMISSIONS, type Permission } from 'modules/auth/permissions';

export interface CreateAction {
  label: string;
  icon: React.ElementType;
  to: string;
}

/** Shared by the desktop menu and the mobile Create FAB, so both stay in sync. */
export function useCreateActions(): CreateAction[] {
  const { t } = useTranslation();
  const {
    state: { permissions: p },
  } = AuthModule.Context.useAuthContext();
  const hasTeams = useFeature(FEATURES.TEAMS);
  const has = (perm: Permission) => p.includes(perm);

  return [
    {
      show: has(PERMISSIONS.TESTS_CREATE),
      label: t('create.new_test', 'New test'),
      icon: ClipboardList,
      to: '/app/tests/create',
    },
    {
      show: has(PERMISSIONS.TESTS_CREATE),
      label: t('create.from_template', 'From a template'),
      icon: LayoutTemplate,
      to: '/app/tests/templates',
    },
    {
      show: has(PERMISSIONS.QUESTIONS_CREATE),
      label: t('create.new_question', 'New question'),
      icon: HelpCircle,
      to: '/app/questions/new',
    },
    {
      show: has(PERMISSIONS.DOCUMENTS_EDIT),
      label: t('create.upload_document', 'Upload document'),
      icon: FileUp,
      to: '/app/documents',
    },
    {
      show: hasTeams && has(PERMISSIONS.TEAMS_CREATE),
      label: t('create.new_group', 'New group'),
      icon: GraduationCap,
      to: '/app/teams',
    },
  ]
    .filter((a) => a.show)
    .map(({ show, ...a }) => a);
}

/** Shown from `md` up. Renders nothing when the member can't create anything. */
export function HeaderCreateMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actions = useCreateActions();

  if (actions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="hidden h-9 gap-1.5 md:inline-flex">
          <Plus className="h-4 w-4" aria-hidden />
          {t('create', 'Create')}
          <ChevronDown className="h-3.5 w-3.5 opacity-75" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-56">
        {actions.map((a) => (
          <DropdownMenuItem key={a.to} onClick={() => navigate(a.to)} className="gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              <a.icon className="h-4 w-4" aria-hidden />
            </span>
            <span className="text-sm">{a.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default HeaderCreateMenu;
